// Component: TradeBandSeries.js
// Purpose: HLC-area style trade level corridor visualization.
//          Renders per-trade isolated bands: green TP zone above entry,
//          red SL zone below entry, white entry line. Follows the official
//          lightweight-charts HLC area plugin pattern with ISeriesPrimitive v5.1 API.

/**
 * TradeBandIndicator — ISeriesPrimitive plugin for lightweight-charts v5.1.
 *
 * Data format: array of trade segments, each an array of { time, sl, tp, entry }.
 * Each segment renders as an isolated HLC area (no continuity between trades).
 *
 * Usage:
 *   const band = new TradeBandIndicator(options);
 *   series.attachPrimitive(band);
 *   band.setData([ tradeSegment1, tradeSegment2, ... ]);
 */
export class TradeBandIndicator {
    constructor(options = {}) {
        this._options = {
            slColor: options.slColor || 'rgba(239, 68, 68, 0.8)',
            tpColor: options.tpColor || 'rgba(16, 185, 129, 0.8)',
            slFillColor: options.slFillColor || 'rgba(239, 68, 68, 0.15)',
            tpFillColor: options.tpFillColor || 'rgba(16, 185, 129, 0.15)',
            entryColor: options.entryColor || '#FFFFFF',
            entryLineWidth: options.entryLineWidth || 2,
            levelLineWidth: options.levelLineWidth || 1,
            //--> Forward projection — extends the band so its right edge sits
            //*   `projectionTrailBars` chart-bars BEYOND the chart's latest
            //*   candle (NOT N bars past the last snapshot timestamp). The
            //*   anchor is the candle series' last data point so the band
            //*   always reaches the live candle regardless of snapshot
            //*   publishing cadence (1m chart with 5m deployment = 5-min gap
            //*   between snapshots, but band still tracks the live bar).
            //*   Off by default; enabled by live/paper callers via setProjection().
            projectionTrailBars: options.projectionTrailBars ?? 0,
            projectionBarSizeSec: options.projectionBarSizeSec ?? 60,
        };
        this._segments = []; // array of arrays (per-trade)
        //--> Parallel array: segments[i] is open iff openFlags[i] is true.
        //*   Only open segments get forward-projected.
        this._openFlags = [];
        this._cachedSegmentPoints = [];
        this._paneViews = [this];
        this._series = null;
        this._chart = null;
        this._requestUpdate = null;
    }

    //--> Lifecycle
    attached({ series, chart, requestUpdate }) {
        this._series = series;
        this._chart = chart;
        this._requestUpdate = requestUpdate;
    }

    detached() {
        this._series = null;
        this._chart = null;
        this._requestUpdate = null;
    }

    /**
     * Set data as array of trade segments.
     * Each segment: [{ time, sl, tp, entry }, ...] for one trade.
     * For single-trade (hover): pass [segment] (one segment in array).
     *
     * Optional `openFlags` parallels segments — true for open trades that should
     * receive forward projection. If omitted, no segment is projected.
     */
    setData(segments, openFlags) {
        this._segments = segments || [];
        this._openFlags = openFlags || [];
        if (this._requestUpdate) this._requestUpdate();
    }

    //--> Configure forward-projection from the caller (live/paper overlay path).
    //*  trailBars: how many chart-bars beyond the chart's right edge the band
    //*             should extend. 0 = no projection. The primitive figures out
    //*             how many synthetic bars to emit from the chart's data.
    //*  barSizeSec: chart bar size in seconds (1m = 60).
    setProjection({ trailBars, barSizeSec }) {
        if (typeof trailBars === 'number') this._options.projectionTrailBars = Math.max(0, trailBars);
        if (typeof barSizeSec === 'number' && barSizeSec > 0) this._options.projectionBarSizeSec = barSizeSec;
        if (this._requestUpdate) this._requestUpdate();
    }

    //--> Update options in-place (e.g. toggle fill colors for scoped_lines vs band mode)
    applyOptions(options) {
        Object.assign(this._options, options);
        if (this._requestUpdate) this._requestUpdate();
    }

    //--> ISeriesPrimitive: recalculate points on viewport change
    updateAllViews() {
        this._cachedSegmentPoints = this._calculateAllSegments();
    }

    //--> ISeriesPrimitive: stable paneViews reference
    paneViews() {
        return this._paneViews;
    }

    //--> IPrimitivePaneView: paint layer. issue-dual-sl-band-render (seam 4, Deepak z-order req):
    //*   'top' — draw the SL/TP risk band ABOVE all series content, including the chandelier-exit
    //*   CUSTOM series (addCustomSeries paints in the series batch, above 'bottom' primitives). 'top'
    //*   is the only PrimitivePaneViewZOrder value with an unconditional above-custom-series guarantee
    //*   ('normal' is order-dependent vs a different custom series). Was 'bottom' → the band painted
    //*   under the chandelier area. Side effect (accepted, NOTE-3): the translucent fill (≤0.15α) now
    //*   also composites over candles + trade markers + crosshair — intended; the band must stay visible.
    zOrder() {
        return 'top';
    }

    //--> IPrimitivePaneView: return renderer
    renderer() {
        const allSegments = this._cachedSegmentPoints;
        if (!allSegments || allSegments.length === 0) return null;

        const options = this._options;

        return {
            draw(target) {
                target.useBitmapCoordinateSpace((scope) => {
                    const ctx = scope.context;
                    const hR = scope.horizontalPixelRatio;
                    const vR = scope.verticalPixelRatio;

                    ctx.save();
                    ctx.lineJoin = 'round';

                    for (const points of allSegments) {
                        if (points.length < 2) continue;

                        //--> Build paths for entry, SL, TP lines (following HLC area pattern)
                        const entryPath = new Path2D();
                        const slPath = new Path2D();
                        const tpPath = new Path2D();

                        const first = points[0];

                        //--> Forward: entry line, SL line, TP line
                        entryPath.moveTo(first.x * hR, first.entryY * vR);
                        if (first.slY != null) slPath.moveTo(first.x * hR, first.slY * vR);
                        if (first.tpY != null) tpPath.moveTo(first.x * hR, first.tpY * vR);

                        for (let i = 1; i < points.length; i++) {
                            const p = points[i];
                            entryPath.lineTo(p.x * hR, p.entryY * vR);
                            if (p.slY != null) slPath.lineTo(p.x * hR, p.slY * vR);
                            if (p.tpY != null) tpPath.lineTo(p.x * hR, p.tpY * vR);
                        }

                        //--> 50-5 R3: an OMS-REJECTED side draws no fill zone and strokes muted + dotted
                        //*   (the flag is uniform per trade -- read off the raw points).
                        const slRejected = points.some(p => p.slRejected);
                        const tpRejected = points.some(p => p.tpRejected);

                        //--> TP fill: area between TP line and entry line (green zone)
                        const tpPoints = points.filter(p => p.tpY != null);
                        if (!tpRejected && tpPoints.length >= 2) {
                            const tpFirst = tpPoints[0];
                            const tpLast = tpPoints[tpPoints.length - 1];
                            const tpArea = new Path2D();
                            tpArea.moveTo(tpFirst.x * hR, tpFirst.tpY * vR);
                            for (let i = 1; i < tpPoints.length; i++) {
                                tpArea.lineTo(tpPoints[i].x * hR, tpPoints[i].tpY * vR);
                            }
                            //--> Close via entry line in reverse
                            tpArea.lineTo(tpLast.x * hR, tpLast.entryY * vR);
                            for (let i = tpPoints.length - 2; i >= 0; i--) {
                                tpArea.lineTo(tpPoints[i].x * hR, tpPoints[i].entryY * vR);
                            }
                            tpArea.closePath();
                            ctx.fillStyle = options.tpFillColor;
                            ctx.fill(tpArea);
                        }

                        //--> SL fill: area between SL line and entry line (red zone)
                        const slPoints = points.filter(p => p.slY != null);
                        if (!slRejected && slPoints.length >= 2) {
                            const slFirst = slPoints[0];
                            const slLast = slPoints[slPoints.length - 1];
                            const slArea = new Path2D();
                            slArea.moveTo(slFirst.x * hR, slFirst.slY * vR);
                            for (let i = 1; i < slPoints.length; i++) {
                                slArea.lineTo(slPoints[i].x * hR, slPoints[i].slY * vR);
                            }
                            //--> Close via entry line in reverse
                            slArea.lineTo(slLast.x * hR, slLast.entryY * vR);
                            for (let i = slPoints.length - 2; i >= 0; i--) {
                                slArea.lineTo(slPoints[i].x * hR, slPoints[i].entryY * vR);
                            }
                            slArea.closePath();
                            ctx.fillStyle = options.slFillColor;
                            ctx.fill(slArea);
                        }

                        //--> Stroke SL boundary line (dashed)
                        const REJECTED_STROKE = 'rgba(156, 163, 175, 0.7)';
                        ctx.strokeStyle = slRejected ? REJECTED_STROKE : options.slColor;
                        ctx.lineWidth = options.levelLineWidth * vR;
                        ctx.setLineDash(slRejected ? [1 * vR, 3 * vR] : [4 * vR, 2 * vR]);
                        ctx.stroke(slPath);

                        //--> Stroke TP boundary line (dashed)
                        ctx.strokeStyle = tpRejected ? REJECTED_STROKE : options.tpColor;
                        ctx.setLineDash(tpRejected ? [1 * vR, 3 * vR] : [4 * vR, 2 * vR]);
                        ctx.stroke(tpPath);
                        ctx.setLineDash([]);

                        //--> Stroke entry line (solid, slightly thicker)
                        ctx.strokeStyle = options.entryColor;
                        ctx.lineWidth = options.entryLineWidth * vR;
                        ctx.stroke(entryPath);
                    }

                    ctx.restore();
                });
            },
        };
    }

    //--> Return null — band should not affect candle price scale
    autoscaleInfo() {
        return null;
    }

    //--> Calculate pixel coordinates per segment
    _calculateAllSegments() {
        if (!this._segments.length || !this._series || !this._chart) return [];

        const timeScale = this._chart.timeScale();
        const result = [];

        for (let segIdx = 0; segIdx < this._segments.length; segIdx++) {
            const segment = this._segments[segIdx];
            if (!segment || segment.length === 0) continue;
            const points = [];
            let lastRawTime = null;
            let lastRawSl = null;
            let lastRawTp = null;
            let lastRawEntry = null;

            const barSec = this._options.projectionBarSizeSec || 60;

            for (const entry of segment) {
                let x = timeScale.timeToCoordinate(entry.time);
                
                // Fallback to nearest logical bar start if exact intra-candle timestamp is unmappable
                if (x === null && entry.time) {
                    const snapped = Math.floor(entry.time / barSec) * barSec;
                    x = timeScale.timeToCoordinate(snapped);
                    
                    if (x === null && barSec !== 60) {
                        const minSnapped = Math.floor(entry.time / 60) * 60;
                        x = timeScale.timeToCoordinate(minSnapped);
                    }
                }

                if (x === null) continue;

                const entryY = entry.entry != null ? this._series.priceToCoordinate(entry.entry) : null;
                if (entryY === null) continue; // entry price is required

                const slY = entry.sl != null ? this._series.priceToCoordinate(entry.sl) : null;
                const tpY = entry.tp != null ? this._series.priceToCoordinate(entry.tp) : null;

                points.push({ x, slY, tpY, entryY, slRejected: !!entry.slRejected, tpRejected: !!entry.tpRejected });
                lastRawTime = entry.time;
                if (entry.sl != null) lastRawSl = entry.sl;
                if (entry.tp != null) lastRawTp = entry.tp;
                if (entry.entry != null) lastRawEntry = entry.entry;
            }

            //--> Back-fill leading null SL/TP from first non-null values
            //    so the band extends to the entry candle even when levels
            //    aren't established until the next bar
            if (points.length >= 1) {
                const firstSl = points.find(p => p.slY != null);
                const firstTp = points.find(p => p.tpY != null);
                for (const p of points) {
                    if (p.slY == null && firstSl) p.slY = firstSl.slY;
                    if (p.tpY == null && firstTp) p.tpY = firstTp.tpY;
                    if (p.slY != null && p.tpY != null) break;
                }
            }

            //--> Forward projection — only for OPEN segments (live/paper).
            //*  Anchored to the chart's right edge (latest candle) so the band
            //*  always reaches the live bar and projects exactly trailBars beyond,
            //*  regardless of how stale the last snapshot is (5m deployment on
            //*  1m chart = up to 5 min between snapshots, but the candle series
            //*  always has the latest 1m bar).
            const isOpen = !!this._openFlags[segIdx];
            const trailBars = this._options.projectionTrailBars;
            if (isOpen && trailBars > 0 && lastRawTime != null && lastRawEntry != null) {
                const barSec = this._options.projectionBarSizeSec;
                //--> Read chart's right-most data bar from the candle series.
                let lastBarTime = null;
                try {
                    const seriesData = this._series.data?.();
                    if (Array.isArray(seriesData) && seriesData.length > 0) {
                        const t = seriesData[seriesData.length - 1].time;
                        lastBarTime = typeof t === 'number' ? t : Number(t);
                    }
                } catch (_e) { /* defensive */ }

                const lastSegTime = typeof lastRawTime === 'number' ? lastRawTime : Number(lastRawTime);
                //--> Target right edge = chart's last bar + trailBars chart-bars.
                //    If chart edge couldn't be resolved (no candles loaded yet),
                //    fall back to "trailBars beyond the last segment time."
                const targetEnd = (lastBarTime != null ? lastBarTime : lastSegTime) + trailBars * barSec;

                if (targetEnd > lastSegTime && barSec > 0) {
                    const entryYProj = this._series.priceToCoordinate(lastRawEntry);
                    const slYProj = lastRawSl != null ? this._series.priceToCoordinate(lastRawSl) : null;
                    const tpYProj = lastRawTp != null ? this._series.priceToCoordinate(lastRawTp) : null;
                    //--> Hard cap on synthetic-bar count. Defends against an
                    //    edge case where lastSegTime is wildly older than the
                    //    chart edge (would otherwise emit thousands of points).
                    const MAX_PROJECTION_BARS = 240;
                    let projTime = lastSegTime + barSec;
                    let emitted = 0;
                    while (projTime <= targetEnd && emitted < MAX_PROJECTION_BARS) {
                        let x = timeScale.timeToCoordinate(projTime);

                        // Allow forward projection into empty chart whitespace (live candle extrapolation)
                        if (x === null && points.length > 0) {
                            try {
                                const lastLogical = timeScale.coordinateToLogical(points[points.length - 1].x);
                                if (lastLogical !== null) {
                                    x = timeScale.logicalToCoordinate(lastLogical + 1);
                                }
                            } catch (_e) { /* defensive */ }
                        }

                        if (x === null) {
                            // Hard physical pixel fallback for 0-width / future whitespace
                            try {
                                const spacing = timeScale.options?.()?.barSpacing || 6;
                                x = points[points.length - 1].x + spacing;
                            } catch (_e) {
                                break;
                            }
                        }
                        if (x === null) break;

                        points.push({
                            x,
                            entryY: entryYProj,
                            slY: slYProj,
                            tpY: tpYProj,
                            _projected: true,
                        });
                        projTime += barSec;
                        emitted += 1;
                    }
                }
            }

            //! Fallback for 0-width or 1-point bands:
            //!  1. Open trade just entered (has exactly 1 point, no projection): append forward pixel point.
            //!  2. Closed trade opened and closed on the exact same candle (all points have the same x coordinate):
            //!     the drawn polygon has 0 width and drops. Append a forward pixel point to give it 1 bar visual width.
            const isZeroWidth = points.length > 0 && points[0].x === points[points.length - 1].x;
            if (isZeroWidth) {
                const only = points[points.length - 1];
                let barSpacing = 6; // Default to 6 physical pixels
                try { barSpacing = timeScale.options?.()?.barSpacing || 6; } catch { /* defensive */ }
                
                points.push({
                    ...only,
                    x: only.x + barSpacing,
                    _projected: isOpen,
                });
            }

            if (points.length >= 2) result.push(points);
        }

        return result;
    }
}
